import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Seo } from '@/components/Seo';
import { ICONS, Icon, InfoShell, usePrefix } from './InfoShell';

// New-design About page (/about): story with a photo, the mission as a
// statement panel, the three values, and the company details as a ruled
// list. Same i18n keys as the classic page.

const splitLabel = (line: string) => {
  const i = line.indexOf(': ');
  return i > 0 && i < 24 ? { k: line.slice(0, i), v: line.slice(i + 2) } : { k: '', v: line };
};

export default function AboutV2() {
  const { t } = useTranslation();
  const prefix = usePrefix();

  const pillars = [
    { icon: ICONS.flask, title: t('about.values.pillar1.title'), desc: t('about.values.pillar1.desc') },
    { icon: ICONS.doc, title: t('about.values.pillar2.title'), desc: t('about.values.pillar2.desc') },
    { icon: ICONS.qr, title: t('about.values.pillar3.title'), desc: t('about.values.pillar3.desc') },
  ];

  const company = [
    { k: t('infoV2.about.entity', 'Operated by'), v: t('about.company.entity', 'Sol Grace Construction BD. Ltd.') },
    splitLabel(t('about.company.bin', 'BIN: 009212412-0108')),
    splitLabel(t('about.company.tin', 'TIN: 520132321674')),
    splitLabel(t('about.company.tradeLicense', 'Trade License: TRAD/DNCC/027028/2025')),
    splitLabel(t('about.company.address', 'Address: 2/2-A, South Begunbari, Tejgaon I/A, Dhaka-1208, Bangladesh')),
  ];

  return (
    <>
      <Seo pageKey="about" />
      <InfoShell label={t('about.hero.label')} title={t('about.hero.heading')} lede={t('about.hero.subtitle')}>
        {/* Story */}
        <section className="isec">
          <div className="wrap astory">
            <figure className="aph rv">
              <img src="/v2/brand-hero.webp" alt="" loading="lazy" />
              <figcaption>
                <span>Seoul</span>
                <Icon d={ICONS.arrow} />
                <span>Dhaka</span>
              </figcaption>
            </figure>
            <div className="atx rv" style={{ ['--d' as string]: '.1s' }}>
              <span className="lbl">{t('about.story.heading')}</span>
              <p className="lead">{t('about.story.p1')}</p>
              <p>{t('about.story.p2')}</p>
              <Link to={prefix + '/brand-story'} className="cmore">
                {t('infoV2.about.brandStory', 'Read the full brand story')}
                <Icon d={ICONS.arrow} />
              </Link>
            </div>
          </div>
        </section>

        {/* Mission */}
        <section className="isec soft">
          <div className="wrap">
            <div className="amis rv">
              <span className="lbl">{t('about.mission.label', 'Our Mission')}</span>
              <h2>{t('about.mission.heading')}</h2>
              <p>{t('about.mission.body')}</p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="isec">
          <div className="wrap">
            <div className="ihd rv">
              <span className="lbl">{t('about.values.label', 'Our Values')}</span>
              <h2>{t('about.values.heading', 'What We Stand For')}</h2>
            </div>
            <div className="apil">
              {pillars.map((p, i) => (
                <article key={i} className="rv" style={{ ['--d' as string]: `${i * 0.08}s` }}>
                  <span className="no">{String(i + 1).padStart(2, '0')}</span>
                  <span className="ic">
                    <Icon d={p.icon} />
                  </span>
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Company */}
        <section className="isec">
          <div className="wrap">
            <div className="acomp rv">
              <div className="acl">
                <span className="lbl">{t('about.company.heading', 'Company Information')}</span>
                <p>{t('about.company.siteDesc')}</p>
                <p className="mut">{t('about.company.concern')}</p>
              </div>
              <dl className="acdl">
                {company.map((c, i) => (
                  <div key={i}>
                    {c.k && <dt>{c.k}</dt>}
                    <dd>{c.v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>
      </InfoShell>
    </>
  );
}
